/* Оболочка мини-аппа: Konsta iOS, нижняя навигация (Tabbar) и маршруты.
   Навигация — 5 пунктов (ТЗ 6, адаптировано под мобильный): Обзор · Финансы · CRM · Алерты · Ещё. */

import { canRead, crmLevel, moreVisible, sectionLevel } from "@mera/shared";
import { App as KonstaApp, Tabbar, TabbarLink, ToolbarPane } from "konsta/react";
import React from "react";
import { Route, Routes, useLocation, useNavigate } from "./lib/router.jsx";

import Assistant from "./components/Assistant.jsx";
import { Ion } from "./components/icons.jsx";
import Screen from "./components/Screen.jsx";
import { ErrorState, Splash } from "./components/ui.jsx";
import { useApp } from "./lib/store.jsx";
import { haptic } from "./lib/tg.js";

import EntityEdit from "./screens/EntityEdit.jsx";
import Manage from "./screens/Manage.jsx";
import Alerts from "./screens/Alerts.jsx";
import BotDemo from "./screens/BotDemo.jsx";
import Businesses from "./screens/Businesses.jsx";
import Cards from "./screens/Cards.jsx";
import Crm from "./screens/Crm.jsx";
import DealCard from "./screens/DealCard.jsx";
import DevTaskCard from "./screens/DevTaskCard.jsx";
import Finance from "./screens/Finance.jsx";
import Instagram from "./screens/Instagram.jsx";
import MeetingCard from "./screens/MeetingCard.jsx";
import Meetings from "./screens/Meetings.jsx";
import More from "./screens/More.jsx";
import Overview from "./screens/Overview.jsx";
import Passwords from "./screens/Passwords.jsx";
import Pdf from "./screens/Pdf.jsx";
import ProjectCard from "./screens/ProjectCard.jsx";
import ProjectPlan from "./screens/ProjectPlan.jsx";
import ScheduleEdit from "./screens/ScheduleEdit.jsx";
import Reports from "./screens/Reports.jsx";
import Shortcut from "./screens/Shortcut.jsx";

/* Иконки везде залитые — активный таб выделяется цветом и подложкой Konsta */
const TABS = [
  { path: "/", label: "Обзор", icon: Ion.gridSolid },
  { path: "/finance", label: "Финансы", icon: Ion.walletSolid },
  { path: "/crm", label: "CRM", icon: Ion.locateSolid },
  { path: "/alerts", label: "Алерты", icon: Ion.notificationsSolid },
  { path: "/more", label: "Ещё", icon: Ion.ellipsisSolid },
];

const TAB_PATHS = TABS.map((t) => t.path);

/* Закрытые администратором разделы в меню не попадают. CRM показывается,
   если открыт общий уровень либо хоть один бизнес с воронкой. «Ещё» — всегда. */
function visibleTabs(me, meta) {
  const crmOk =
    canRead(crmLevel(me, null)) ||
    (meta?.businesses || []).some((b) => canRead(crmLevel(me, b.id)));
  return TABS.filter(({ path }) =>
    path === "/more"
    || (path === "/" && canRead(sectionLevel(me, "overview")))
    || (path === "/finance" && canRead(sectionLevel(me, "finance")))
    || (path === "/crm" && crmOk)
    || (path === "/alerts" && canRead(sectionLevel(me, "alerts"))));
}

/* Стартовый экран: главная, а если она скрыта — первый доступный раздел */
const homePathOf = (tabs) => (tabs.some((t) => t.path === "/") ? "/" : tabs[0]?.path || "/more");

function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { me, meta, alerts } = useApp();
  const counts = alerts?.counts || { critical: 0, soft: 0 };

  const tabs = visibleTabs(me, meta);
  const homePath = homePathOf(tabs);

  // Вложенные экраны (карточки) прячут таббар, чтобы освободить высоту
  if (!TAB_PATHS.includes(pathname)) return null;

  // ToolbarPane даёт подсветку активного таба; размытие фона Konsta снимаем — backdrop-blur-none
  return (
    <Tabbar labels icons className="left-0 bottom-0 fixed">
      <ToolbarPane className="backdrop-blur-none">
        {tabs.map(({ path, label, icon: Icon }) => {
          // Корень рисует первый доступный раздел — подсвечиваем его же
          const active = pathname === path || (pathname === "/" && path === homePath);
          const isAlerts = path === "/alerts";
          return (
            <TabbarLink
              key={path}
              active={active}
              onClick={() => {
                haptic.select();
                navigate(path);
              }}
              icon={
                <span className="relative inline-flex">
                  <Icon size={24} />
                  {isAlerts && counts.critical > 0 ? (
                    <Badge color="var(--fin-red)" value={counts.critical} />
                  ) : null}
                  {isAlerts && counts.critical === 0 && counts.soft > 0 ? (
                    <Badge color="var(--fin-gold)" value={counts.soft} />
                  ) : null}
                </span>
              }
              label={<span className="text-[10px] font-semibold">{label}</span>}
            />
          );
        })}
      </ToolbarPane>
    </Tabbar>
  );
}

/* Два бейджа колокольчика: красный — критичные, жёлтый — мягкие (ТЗ 6.10) */
function Badge({ color, value }) {
  return (
    <span
      className="num absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
      style={{ background: color }}
    >
      {value > 99 ? "99+" : value}
    </span>
  );
}

const SCREEN_OF = { "/": Overview, "/finance": Finance, "/crm": Crm, "/alerts": Alerts, "/more": More };

/* Экраны блоков «Ещё» открываются не только из списка — на встречу или на шорткат
   ведут ссылки из бота, поэтому скрытый блок закрываем и на самом маршруте. */
function MoreGate({ block, children }) {
  const { me } = useApp();
  if (moreVisible(me, block)) return children;
  return <Screen back><ErrorState text="Этот раздел вам закрыт. Права выдаёт администратор." /></Screen>;
}

export default function App() {
  const { me, meta, ready, error, reload } = useApp();

  // Главная может быть скрыта правами — тогда корень открывает первый доступный раздел
  const Home = SCREEN_OF[homePathOf(visibleTabs(me, meta))] || More;

  return (
    <KonstaApp theme="ios" safeAreas={false}>
      {!ready ? (
        <Splash />
      ) : error ? (
        <div className="pt-16">
          <ErrorState text={error} onRetry={reload} />
        </div>
      ) : (
        <>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/crm" element={<Crm />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/more" element={<More />} />
            <Route path="/businesses" element={<Businesses />} />
            <Route path="/manage/:kind" element={<Manage />} />
            <Route path="/edit/:kind/:id" element={<EntityEdit />} />
            <Route path="/reports" element={<MoreGate block="reports"><Reports /></MoreGate>} />
            <Route path="/instagram" element={<MoreGate block="tools"><Instagram /></MoreGate>} />
            <Route path="/meetings" element={<MoreGate block="tools"><Meetings /></MoreGate>} />
            <Route path="/meeting/:id" element={<MoreGate block="tools"><MeetingCard /></MoreGate>} />
            <Route path="/pdf" element={<MoreGate block="tools"><Pdf /></MoreGate>} />
            <Route path="/passwords" element={<MoreGate block="vault"><Passwords /></MoreGate>} />
            <Route path="/cards" element={<MoreGate block="vault"><Cards /></MoreGate>} />
            <Route path="/shortcut" element={<MoreGate block="shortcut"><Shortcut /></MoreGate>} />
            <Route path="/bot" element={<BotDemo />} />
            <Route path="/deal/:id" element={<DealCard />} />
            <Route path="/project/:id" element={<ProjectCard />} />
            <Route path="/project/:id/plan" element={<ProjectPlan />} />
            <Route path="/schedule/:id" element={<ScheduleEdit />} />
            <Route path="/devtask/:id" element={<DevTaskCard />} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Assistant />
          <BottomNav />
        </>
      )}
    </KonstaApp>
  );
}
